/* eslint-disable */
import {
	createSlice,
	type PayloadAction,
} from "@reduxjs/toolkit";
import { isEqual } from "lodash";

import {
	DatasetStepEnum,
	type DatasetCreationTypeEnum,
	type TLedgerSanitizationState,
} from "../../types/ledger.type";
import {
	buildFallbackAvailableColsBySource,
	flattenAvailableCols,
	groupAvailableColsBySource,
} from "../../utils/availableColumnsUtils";
import {
	allDatasetStepMapping,
	extractQuestionsDataset,
	getDatasetSanitizationSteps,
} from "../../utils/ledgerUtils";

import {
	fetchDatasetSpecsQuestionsThunk,
	fetchDatasetStatsThunk,
	getDatasetMetaThunk,
	getGeneralMetaDataThunk,
	getPerfColumnMappingDataThunk,
	getPerfDataFromDataRange,
	getPerfDataRangeThunk,
	getStaticColumnMappingDataThunk,
	getStaticDataFromDataRange,
	getStaticDataRangeThunk,
	updatePerfColumnMappingDataThunk,
	updateStaticColumnMappingDataThunk,
	uploadFileThunk,
	getProcessingFiltersThunk,
	addProcessingFilterThunk,
	updateProcessingFilterThunk,
	deleteProcessingFilterThunk,
	getPerfProcessingFiltersThunk,
	addPerfProcessingFilterThunk,
	updatePerfProcessingFilterThunk,
	deletePerfProcessingFilterThunk,
} from "./ledgerSanitization.thunk";

const getComparableNumber = (
	value: unknown,
	fallback: number
): number => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
};

const sortProcessingFilters = (filters: Array<any>) => {
	return [...filters].sort((a: any, b: any) => {
		const orderA = getComparableNumber(
			a?.order,
			Number.MAX_SAFE_INTEGER
		);
		const orderB = getComparableNumber(
			b?.order,
			Number.MAX_SAFE_INTEGER
		);
		if (orderA !== orderB) {
			return orderA - orderB;
		}

		const createdAtA = getComparableNumber(
			a?.created_at,
			0
		);
		const createdAtB = getComparableNumber(
			b?.created_at,
			0
		);
		if (createdAtA !== createdAtB) {
			return createdAtA - createdAtB;
		}

		const updatedAtA = getComparableNumber(
			a?.updated_at,
			0
		);
		const updatedAtB = getComparableNumber(
			b?.updated_at,
			0
		);
		if (updatedAtA !== updatedAtB) {
			return updatedAtA - updatedAtB;
		}

		return String(a?.filter_id ?? "").localeCompare(
			String(b?.filter_id ?? "")
		);
	});
};

const parseAndSortProcessingFilters = (
	filters: unknown
) => {
	const parsedFilters = Array.isArray(filters)
		? filters
		: Object.values(
				(filters as Record<string, unknown>) || {}
			);
	return sortProcessingFilters(parsedFilters);
};

const getColumnMappingColumns = (
	payload: any
): Array<any> => {
	if (Array.isArray(payload)) {
		return payload;
	}
	if (Array.isArray(payload?.user_defined_cols)) {
		return payload.user_defined_cols;
	}
	if (Array.isArray(payload?.allColumns)) {
		return payload.allColumns;
	}
	return [];
};

const getColumnMappingAvailableColsBySource = (
	payload: any
): Record<string, Array<string>> => {
	const groupedAvailableCols = groupAvailableColsBySource(
		payload?.available_cols
	);
	if (Object.keys(groupedAvailableCols).length > 0) {
		return groupedAvailableCols;
	}

	if (Array.isArray(payload?.raw_cols?.all_cols)) {
		return buildFallbackAvailableColsBySource(
			payload.raw_cols.all_cols
		);
	}
	if (Array.isArray(payload?.raw_cols)) {
		return buildFallbackAvailableColsBySource(
			payload.raw_cols
		);
	}
	return {};
};

const getColumnMappingAvailableCols = (
	payload: any
): Array<string> => {
	if (Array.isArray(payload?.availableCols)) {
		return payload.availableCols;
	}

	const flattenedAvailableCols = flattenAvailableCols(
		payload?.available_cols
	);
	if (flattenedAvailableCols.length > 0) {
		return flattenedAvailableCols;
	}

	if (Array.isArray(payload?.raw_cols?.all_cols)) {
		return payload.raw_cols.all_cols;
	}
	if (Array.isArray(payload?.raw_cols)) {
		return payload.raw_cols;
	}
	return [];
};

const mapColumnMappingState = (payload: any) => {
	return {
		allColumns: getColumnMappingColumns(payload),
		availableCols: getColumnMappingAvailableCols(payload),
		availableColsBySource:
			getColumnMappingAvailableColsBySource(payload),
	};
};

const resetStates = (keys: Array<string>, state: any) => {
	keys.forEach((key) => {
		if (state[key].data !== null) {
			state[key] =
				initialState[key as keyof TLedgerSanitizationState];
		}
	});
};

const initialState: TLedgerSanitizationState = {
	activeDatasetMeta: null,
	allSteps: Object.values(DatasetStepEnum),
	method: null,
	activeStep: DatasetStepEnum.STEP_DATASET_SPECS,
	nextStep: null,
	staticDatasetUploadStatus: {
		status: "not-started",
		error: null,
		loading: false,
	},
	perfDatasetUploadStatus: {
		status: "not-started",
		error: null,
		loading: false,
	},
	generalMeta: null,
	tapesLoad: null,
	datasetSanitizationMeta: null,
	datasetSanitizationMetaStatus: {
		loading: false,
		error: null,
	},

	staticDataRangeSelection: null,
	staticDataRangeStatus: {
		loading: false,
		error: null,
		shortPreviewLoading: false,
	},
	staticColumnMappingStatus: {
		loading: false,
		error: null,
		previewLoading: false,
	},
	staticColumnMapping: null,

	perfDataRangeSelection: null,
	perfDataRangeStatus: {
		loading: false,
		error: null,
		shortPreviewLoading: false,
	},
	perfColumnMappingStatus: {
		loading: false,
		error: null,
		previewLoading: false,
	},
	perfColumnMapping: null,

	specsQuestions: {
		data: null,
		loading: false,
		error: null,
	},
	reviewStats: {
		data: null,
		loading: false,
		error: null,
	},

	processingFilters: {
		preProcessingFilters: [],
		postProcessingFilters: [],
		loading: false,
		error: null,
		saveLoading: false,
		datasetId: null,
		hasFetched: false,
	},

	perfProcessingFilters: {
		preProcessingFilters: [],
		postProcessingFilters: [],
		loading: false,
		error: null,
		saveLoading: false,
		datasetId: null,
		hasFetched: false,
	},

	error: null,
	submissionStatus: null,
};

const ledgerSanitizationSlice = createSlice({
	name: "ledgerSanitization",
	initialState,
	reducers: {
		resetDatasetUpload: (
			state,
			action: PayloadAction<boolean>
		) => {
			const statusKey = action.payload
				? "staticDatasetUploadStatus"
				: "perfDatasetUploadStatus";
			state[statusKey] = {
				status: "not-started",
				loading: false,
				error: null,
			};
			if (action.payload) {
				state.staticDataRangeSelection = null;
			} else {
				state.perfDataRangeSelection = null;
			}
		},
		resetEverything: () => {
			return { ...initialState };
		},
		updateActiveDatasetMeta: (state, action) => {
			state.activeDatasetMeta = action.payload;
		},
		updateRangeSelection: (state, action) => {
			const { sheet, headerRange, dataRange, isStatic } =
				action.payload;
			if (isStatic) {
				if (state.staticDataRangeSelection) {
					state.staticDataRangeSelection.recentRangeSelection =
						{
							sheet,
							headerRange,
							dataRange,
						};
				}
			} else {
				if (state.perfDataRangeSelection) {
					state.perfDataRangeSelection.recentRangeSelection =
						{
							sheet,
							headerRange,
							dataRange,
						};
				}
			}
		},
		updateCurrentStep: (state, action) => {
			state.tapesLoad = action.payload.tapes_load ?? null;
			if (state.datasetSanitizationMeta) {
				const currentStepIndex =
					action.payload.all_steps.findIndex(
						(el: string) =>
							el === action.payload.current_step
					);

				const datasetStepMapping =
					getDatasetSanitizationSteps(
						state.method as DatasetCreationTypeEnum
					);

				state.datasetSanitizationMeta = {
					allSteps:
						action.payload.all_steps.map(
							(el: string, index: number) => {
								const step =
									datasetStepMapping[el as DatasetStepEnum];
								return {
									...step,
									stepId: el as DatasetStepEnum,
									isCompleted: currentStepIndex > index,
								};
							}
						) ?? [],
					currentStep: {
						...datasetStepMapping[
							action.payload.current_step as DatasetStepEnum
						],
						stepId: action.payload
							.current_step as DatasetStepEnum,
						isCompleted: false,
					},
					datasetName: action.payload.dataset_name,
					isApproved:
						action.payload.current_step ===
						DatasetStepEnum.STEP_DATASET_COMPLETE,
				};
				state.submissionStatus = {
					currentStep:
						state.datasetSanitizationMeta.currentStep
							.stepId,
					nextStep: state.datasetSanitizationMeta
						.currentStep.nextStep as DatasetStepEnum,
					submissionCreated: false,
					isSubmissionSuccessful: false,
					error: null,
				};
			}
		},
		updateStaticColumnMappingData: (state, action) => {
			state.staticColumnMapping = mapColumnMappingState(
				action.payload
			);
		},
		updatePerfColumnMappingData: (state, action) => {
			state.perfColumnMapping = mapColumnMappingState(
				action.payload
			);
		},
		updateSanitizationSubmissionStatus: (state, action) => {
			state.submissionStatus = action.payload;
		},
		markReviewStatsInProgress: (state) => {
			state.reviewStats.data = {
				status: "IN_PROGRESS",
			};
			state.reviewStats.loading = false;
			state.reviewStats.error = null;
		},
		updateActiveStep: (state, action) => {
			state.activeStep = action.payload;
		},
		updateCurrentSchemaId: (state, action) => {
			if (state.activeDatasetMeta) {
				state.activeDatasetMeta.currentSchemaId =
					action.payload;
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(uploadFileThunk.pending, (state) => {
				if (
					state.activeStep ===
					DatasetStepEnum.STEP_STATIC_UPLOAD
				) {
					state.staticDatasetUploadStatus.status =
						"in-progress";
					state.staticDatasetUploadStatus.loading = true;
					state.staticDatasetUploadStatus.error = null;
				} else if (
					state.activeStep ===
					DatasetStepEnum.STEP_PERF_UPLOAD
				) {
					state.perfDatasetUploadStatus.status =
						"in-progress";
					state.perfDatasetUploadStatus.loading = true;
					state.perfDatasetUploadStatus.error = null;
				}
			})
			.addCase(
				uploadFileThunk.fulfilled,
				(state, action) => {
					const payload = action.payload;

					const allSheets = Object.keys(
						payload.sheet_names
					);
					const previewRange = payload.preview_data_range;
					const selectedSheet = previewRange.sheet_name;

					state.nextStep = payload.next_step;

					if (
						state.activeStep ===
						DatasetStepEnum.STEP_STATIC_UPLOAD
					) {
						state.staticDatasetUploadStatus.status =
							"completed";
						state.staticDatasetUploadStatus.loading = false;
						state.staticDatasetUploadStatus.error = null;
						state.error = null;

						state.staticDataRangeSelection = {
							sheets: allSheets,
							selectedSheet,
							allSheetsData: Object.entries(
								payload.sheet_names
							).map(([key, value]: any) => {
								return {
									sheetName: key,
									meta: value,
								};
							}),
							suggestedRange: null,
						};
					} else if (
						state.activeStep ===
						DatasetStepEnum.STEP_PERF_UPLOAD
					) {
						state.perfDatasetUploadStatus.status =
							"completed";
						state.perfDatasetUploadStatus.loading = false;
						state.perfDatasetUploadStatus.error = null;
						state.error = null;

						state.perfDataRangeSelection = {
							sheets: allSheets,
							selectedSheet,
							allSheetsData: Object.entries(
								payload.sheet_names
							).map(([key, value]: any) => {
								return {
									sheetName: key,
									meta: value,
								};
							}),
							suggestedRange: null,
						};
					}
				}
			)
			.addCase(
				uploadFileThunk.rejected,
				(state, action) => {
					if (
						state.activeStep ===
						DatasetStepEnum.STEP_STATIC_UPLOAD
					) {
						state.staticDatasetUploadStatus.loading = false;
						state.staticDatasetUploadStatus.status =
							"failed";
						state.staticDatasetUploadStatus.error =
							action.payload as any;
					} else if (
						state.activeStep ===
						DatasetStepEnum.STEP_PERF_UPLOAD
					) {
						state.perfDatasetUploadStatus.loading = false;
						state.perfDatasetUploadStatus.status = "failed";
						state.perfDatasetUploadStatus.error =
							action.payload as any;
					}
				}
			);

		builder
			.addCase(getGeneralMetaDataThunk.pending, (state) => {
				state.error = null;
			})
			.addCase(
				getGeneralMetaDataThunk.fulfilled,
				(state, action) => {
					state.generalMeta = action.payload;
				}
			)
			.addCase(
				getGeneralMetaDataThunk.rejected,
				(state, action) => {
					state.error = action.payload as string;
				}
			);

		builder
			.addCase(getDatasetMetaThunk.pending, (state) => {
				state.datasetSanitizationMetaStatus.loading = true;
				state.datasetSanitizationMetaStatus.error = null;
			})
			.addCase(
				getDatasetMetaThunk.fulfilled,
				(state, action) => {
					state.tapesLoad =
						action.payload.tapes_load ?? null;
					if (
						action.payload.all_steps &&
						action.payload.current_step
					) {
						state.activeDatasetMeta = {
							datasetId: action.payload.dataset_id,
							datasetName: action.payload.dataset_name,
							signatures: Array.isArray(
								action.payload.signatures
							)
								? {}
								: (action.payload.signatures ?? {}),
							currentSchemaId:
								action.payload.current_schema_id ?? "",
						};

						const method = action.payload.dataset_type;

						state.method =
							method as DatasetCreationTypeEnum;

						const datasetStepMapping =
							getDatasetSanitizationSteps(
								method as DatasetCreationTypeEnum
							);

						const currentStepIndex =
							action.payload.all_steps.findIndex(
								(el: string) =>
									el === action.payload.current_step
							);

						state.datasetSanitizationMeta = {
							allSteps:
								action.payload.all_steps.map(
									(el: string, index: number) => {
										const step =
											datasetStepMapping[
												el as DatasetStepEnum
											];
										return {
											...step,
											stepId: el as DatasetStepEnum,
											isCompleted: currentStepIndex > index,
										};
									}
								) ?? [],
							currentStep: {
								...datasetStepMapping[
									action.payload
										.current_step as DatasetStepEnum
								],
								stepId: action.payload
									.current_step as DatasetStepEnum,
								isCompleted: false,
							},
							datasetName: action.payload.dataset_name,
							isApproved:
								action.payload.current_step ===
								DatasetStepEnum.STEP_DATASET_COMPLETE,
						};
					} else {
						state.datasetSanitizationMeta = {
							allSteps: [],
							currentStep: {
								...allDatasetStepMapping[
									DatasetStepEnum.STEP_DATASET_COMPLETE
								],
								stepId:
									DatasetStepEnum.STEP_DATASET_COMPLETE,
								isCompleted: true,
							},
							datasetName: "",
							isApproved: false,
						};
					}
					state.datasetSanitizationMetaStatus.loading =
						false;
					state.datasetSanitizationMetaStatus.error = null;
				}
			)
			.addCase(
				getDatasetMetaThunk.rejected,
				(state, action) => {
					state.datasetSanitizationMetaStatus.loading =
						false;
					state.datasetSanitizationMetaStatus.error =
						action.payload ?? action.error;
					state.datasetSanitizationMeta = null;
					state.activeDatasetMeta = null;
				}
			);

		builder
			.addCase(getStaticDataRangeThunk.pending, (state) => {
				state.error = null;
				state.staticDataRangeStatus.loading = true;
				state.staticDataRangeStatus.error = null;
				state.staticDataRangeStatus.statusCode = undefined;
				state.staticDataRangeStatus.shortPreviewLoading =
					true;
			})
			.addCase(
				getStaticDataRangeThunk.fulfilled,
				(state, action) => {
					state.error = null;

					const payload = action.payload;

					const preview = action.payload.preview_data_range;

					const suggestion = {
						sheetName: preview.sheet_name,
						headerRange: preview.header_range,
						bodyRange: preview.body_range,
					};

					const allSheets = Object.keys(
						payload.sheet_bounds
					);
					const selectedSheet = suggestion.sheetName;

					const confirmData = payload.confirmed_data_range;
					const isPreviouslyConfirmed =
						Boolean(confirmData);
					const isModifiedAndConfirmed = isEqual(
						confirmData,
						preview
					);

					state.staticDataRangeSelection = {
						sheets: allSheets,
						selectedSheet,
						allSheetsData: Object.entries(
							payload.sheet_bounds
						).map(([key, value]: any) => {
							return {
								sheetName: key,
								meta: value,
							};
						}),
						suggestedRange: suggestion,
						recentRangeSelection: {
							sheet: suggestion.sheetName,
							headerRange: suggestion.headerRange,
							dataRange: suggestion.bodyRange,
						},
						isStepConfirmed: isPreviouslyConfirmed,
						isModifiedNotConfirmed: !isModifiedAndConfirmed,
					};
					state.staticDataRangeStatus.loading = false;
					state.staticDataRangeStatus.error = null;
					state.staticDataRangeStatus.statusCode = undefined;
					state.staticDataRangeStatus.shortPreviewLoading =
						false;
				}
			)
			.addCase(
				getStaticDataRangeThunk.rejected,
				(state, action) => {
					state.error = action.payload as string;
					state.staticDataRangeSelection = null;
					state.staticDataRangeStatus.loading = false;
					// payload is the AlloanException body ({error_code, msg, ...}); store only the
					// message string, never the object (it gets rendered as a React child).
					state.staticDataRangeStatus.error =
						(action.payload as any)?.msg ??
						"Failed to load data range";
					state.staticDataRangeStatus.statusCode =
						(action.payload as any)?.status;
					state.staticDataRangeStatus.shortPreviewLoading =
						false;
				}
			);

		builder
			.addCase(getPerfDataRangeThunk.pending, (state) => {
				state.error = null;
				state.perfDataRangeStatus.loading = true;
				state.perfDataRangeStatus.error = null;
				state.perfDataRangeStatus.statusCode = undefined;
				state.perfDataRangeStatus.shortPreviewLoading =
					true;
			})
			.addCase(
				getPerfDataRangeThunk.fulfilled,
				(state, action) => {
					state.error = null;

					const payload = action.payload;

					const preview = action.payload.preview_data_range;

					const suggestion = {
						sheetName: preview.sheet_name,
						headerRange: preview.header_range,
						bodyRange: preview.body_range,
					};

					const allSheets = Object.keys(
						payload.sheet_bounds
					);
					const selectedSheet = suggestion.sheetName;

					const confirmData = payload.confirmed_data_range;
					const isPreviouslyConfirmed =
						Boolean(confirmData);
					const isModifiedAndConfirmed = isEqual(
						confirmData,
						preview
					);

					state.perfDataRangeSelection = {
						sheets: allSheets,
						selectedSheet,
						allSheetsData: Object.entries(
							payload.sheet_bounds
						).map(([key, value]: any) => {
							return {
								sheetName: key,
								meta: value,
							};
						}),
						suggestedRange: suggestion,
						recentRangeSelection: {
							sheet: suggestion.sheetName,
							headerRange: suggestion.headerRange,
							dataRange: suggestion.bodyRange,
						},
						isStepConfirmed: isPreviouslyConfirmed,
						isModifiedNotConfirmed: !isModifiedAndConfirmed,
					};
					state.perfDataRangeStatus.loading = false;
					state.perfDataRangeStatus.error = null;
					state.perfDataRangeStatus.statusCode = undefined;
					state.perfDataRangeStatus.shortPreviewLoading =
						false;
				}
			)
			.addCase(
				getPerfDataRangeThunk.rejected,
				(state, action) => {
					state.error = action.payload as string;
					state.perfDataRangeSelection = null;
					state.perfDataRangeStatus.loading = false;
					// payload is the AlloanException body ({error_code, msg, ...}); store only the
					// message string, never the object (it gets rendered as a React child).
					state.perfDataRangeStatus.error =
						(action.payload as any)?.msg ??
						"Failed to load data range";
					state.perfDataRangeStatus.statusCode =
						(action.payload as any)?.status;
					state.perfDataRangeStatus.shortPreviewLoading =
						false;
				}
			);

		builder
			.addCase(
				getStaticDataFromDataRange.pending,
				(state) => {
					state.staticDataRangeStatus.loading = true;
					state.error = null;
					state.staticDataRangeStatus.shortPreviewLoading =
						true;
					state.staticDataRangeStatus.error = null;
					state.staticDataRangeStatus.statusCode =
						undefined;
				}
			)
			.addCase(
				getStaticDataFromDataRange.fulfilled,
				(state, action) => {
					const payload = action.payload;
					state.staticDataRangeStatus.loading = false;
					state.error = null;
					if (
						state.staticDataRangeSelection &&
						(Array.isArray(payload) || payload.table)
					) {
						state.staticDataRangeSelection.shortPreview =
							Array.isArray(payload)
								? payload
								: payload.table;
					}
					state.staticDataRangeStatus.shortPreviewLoading =
						false;
					state.staticDataRangeStatus.error = null;
					state.staticDataRangeStatus.statusCode =
						undefined;
				}
			)
			.addCase(
				getStaticDataFromDataRange.rejected,
				(state, action) => {
					state.staticDataRangeStatus.loading = false;
					state.error = action.payload as string;
					state.staticDataRangeStatus.shortPreviewLoading =
						false;
					state.staticDataRangeStatus.error = (
						action.payload as any
					)?.msg as string;

					state.staticDataRangeStatus.statusCode = (
						action.payload as any
					).status;
					if (state.staticDataRangeSelection) {
						state.staticDataRangeSelection.shortPreview =
							undefined;
					}
				}
			);

		builder
			.addCase(
				getPerfDataFromDataRange.pending,
				(state) => {
					state.perfDataRangeStatus.loading = true;
					state.error = null;
					state.perfDataRangeStatus.shortPreviewLoading =
						true;
					state.perfDataRangeStatus.error = null;
					state.perfDataRangeStatus.statusCode = undefined;
				}
			)
			.addCase(
				getPerfDataFromDataRange.fulfilled,
				(state, action) => {
					const payload = action.payload;
					state.perfDataRangeStatus.loading = false;
					state.error = null;
					if (
						state.perfDataRangeSelection &&
						payload.table
					) {
						state.perfDataRangeSelection.shortPreview =
							payload.table;
					}
					state.perfDataRangeStatus.shortPreviewLoading =
						false;
					state.perfDataRangeStatus.error = null;
					state.perfDataRangeStatus.statusCode = undefined;
				}
			)
			.addCase(
				getPerfDataFromDataRange.rejected,
				(state, action) => {
					state.perfDataRangeStatus.loading = false;
					state.error = action.payload as string;
					state.perfDataRangeStatus.shortPreviewLoading =
						false;
					state.perfDataRangeStatus.error = (
						action.payload as any
					)?.msg as string;

					state.perfDataRangeStatus.statusCode = (
						action.payload as any
					).status;
					if (state.perfDataRangeSelection) {
						state.perfDataRangeSelection.shortPreview =
							undefined;
					}
				}
			);

		builder
			.addCase(
				getStaticColumnMappingDataThunk.pending,
				(state) => {
					state.staticColumnMappingStatus.loading = true;
					state.error = null;
				}
			)
			.addCase(
				getStaticColumnMappingDataThunk.fulfilled,
				(state, action) => {
					state.staticColumnMappingStatus.loading = false;
					state.error = null;
					if (action.payload) {
						state.staticColumnMapping =
							mapColumnMappingState(action.payload);
					}
				}
			)
			.addCase(
				getStaticColumnMappingDataThunk.rejected,
				(state, action) => {
					state.staticColumnMappingStatus.loading = false;
					state.error = action.payload as string;
				}
			);

		builder
			.addCase(
				getPerfColumnMappingDataThunk.pending,
				(state) => {
					state.perfColumnMappingStatus.loading = true;
					state.error = null;
				}
			)
			.addCase(
				getPerfColumnMappingDataThunk.fulfilled,
				(state, action) => {
					state.perfColumnMappingStatus.loading = false;
					state.error = null;
					if (action.payload) {
						state.perfColumnMapping = mapColumnMappingState(
							action.payload
						);
					}
				}
			)
			.addCase(
				getPerfColumnMappingDataThunk.rejected,
				(state, action) => {
					state.perfColumnMappingStatus.loading = false;
					state.error = action.payload as string;
				}
			);

		builder
			.addCase(
				updateStaticColumnMappingDataThunk.pending,
				(state) => {
					state.error = null;
					state.staticColumnMappingStatus.previewLoading =
						true;
				}
			)
			.addCase(
				updateStaticColumnMappingDataThunk.fulfilled,
				(state, action) => {
					state.error = null;
					if (!action.payload.isStepConfirmed) {
						state.staticColumnMapping =
							mapColumnMappingState(
								action.payload.response
							);
					}
					state.staticColumnMappingStatus.previewLoading =
						false;
				}
			)
			.addCase(
				updateStaticColumnMappingDataThunk.rejected,
				(state, action) => {
					state.staticColumnMappingStatus.previewLoading =
						false;
					state.error = action.payload as string;
				}
			);

		builder
			.addCase(
				updatePerfColumnMappingDataThunk.pending,
				(state) => {
					state.error = null;
					state.perfColumnMappingStatus.previewLoading =
						true;
				}
			)
			.addCase(
				updatePerfColumnMappingDataThunk.fulfilled,
				(state, action) => {
					state.error = null;
					if (!action.payload.isStepConfirmed) {
						state.perfColumnMapping = mapColumnMappingState(
							action.payload.response
						);
					}
					state.perfColumnMappingStatus.previewLoading =
						false;
				}
			)
			.addCase(
				updatePerfColumnMappingDataThunk.rejected,
				(state, action) => {
					state.perfColumnMappingStatus.previewLoading =
						false;
					state.error = action.payload as string;
				}
			);

		builder
			.addCase(
				fetchDatasetSpecsQuestionsThunk.pending,
				(state) => {
					state.specsQuestions.loading = true;
					state.specsQuestions.error = null;
					const keys = ["specsQuestions"];
					resetStates(keys, state);
				}
			)
			.addCase(
				fetchDatasetSpecsQuestionsThunk.fulfilled,
				(state, action) => {
					const questions = extractQuestionsDataset(
						action.payload
					);
					state.specsQuestions.data = questions;

					state.specsQuestions.loading = false;
					state.specsQuestions.error = null;
				}
			)
			.addCase(
				fetchDatasetSpecsQuestionsThunk.rejected,
				(state, action) => {
					state.specsQuestions.error = action.payload;
					state.specsQuestions.loading = false;
				}
			);
		builder
			.addCase(fetchDatasetStatsThunk.pending, (state) => {
				state.reviewStats.loading = true;
				state.reviewStats.error = null;
			})
			.addCase(
				fetchDatasetStatsThunk.fulfilled,
				(state, action) => {
					state.reviewStats.data = action.payload;
					state.reviewStats.loading = false;
					state.reviewStats.error = null;
				}
			)
			.addCase(
				fetchDatasetStatsThunk.rejected,
				(state, action) => {
					state.reviewStats.data = null;
					state.reviewStats.loading = false;
					state.reviewStats.error = action.payload;
				}
			);

		builder
			.addCase(
				getProcessingFiltersThunk.pending,
				(state, action) => {
					const requestedDatasetId = action.meta.arg;
					const isDatasetChanged =
						state.processingFilters.datasetId !==
						requestedDatasetId;

					if (isDatasetChanged) {
						state.processingFilters.preProcessingFilters =
							[];
						state.processingFilters.postProcessingFilters =
							[];
						state.processingFilters.hasFetched = false;
					}

					state.processingFilters.datasetId =
						requestedDatasetId;
					state.processingFilters.loading = true;
					state.processingFilters.error = null;
				}
			)
			.addCase(
				getProcessingFiltersThunk.fulfilled,
				(state, action) => {
					const pre =
						action.payload.pre_processing_filters || {};
					const post =
						action.payload.post_processing_filters || {};
					state.processingFilters.preProcessingFilters =
						parseAndSortProcessingFilters(pre);
					state.processingFilters.postProcessingFilters =
						parseAndSortProcessingFilters(post);
					state.processingFilters.datasetId =
						action.meta.arg;
					state.processingFilters.hasFetched = true;
					state.processingFilters.loading = false;
					state.processingFilters.error = null;
				}
			)
			.addCase(
				getProcessingFiltersThunk.rejected,
				(state, action) => {
					state.processingFilters.datasetId =
						action.meta.arg;
					state.processingFilters.hasFetched = false;
					state.processingFilters.loading = false;
					state.processingFilters.error =
						(action.payload as any)?.msg ||
						"Error loading filters";
				}
			);

		builder
			.addCase(
				addProcessingFilterThunk.pending,
				(state) => {
					state.processingFilters.saveLoading = true;
				}
			)
			.addCase(
				addProcessingFilterThunk.fulfilled,
				(state, action) => {
					const newFilter = action.payload.filter;
					if (newFilter.filter_type === "pre_processing") {
						state.processingFilters.preProcessingFilters.push(
							newFilter
						);
						state.processingFilters.preProcessingFilters =
							sortProcessingFilters(
								state.processingFilters.preProcessingFilters
							);
					} else {
						state.processingFilters.postProcessingFilters.push(
							newFilter
						);
						state.processingFilters.postProcessingFilters =
							sortProcessingFilters(
								state.processingFilters
									.postProcessingFilters
							);
					}
					state.processingFilters.saveLoading = false;
				}
			)
			.addCase(
				addProcessingFilterThunk.rejected,
				(state) => {
					state.processingFilters.saveLoading = false;
				}
			);

		builder
			.addCase(
				updateProcessingFilterThunk.pending,
				(state) => {
					state.processingFilters.saveLoading = true;
				}
			)
			.addCase(
				updateProcessingFilterThunk.fulfilled,
				(state, action) => {
					const updatedFilter = action.payload.filter;
					const filterType = updatedFilter.filter_type;
					const filterKey =
						filterType === "pre_processing"
							? "preProcessingFilters"
							: "postProcessingFilters";

					const index = state.processingFilters[
						filterKey
					].findIndex(
						(f: any) =>
							f.filter_id === updatedFilter.filter_id
					);
					if (index !== -1) {
						state.processingFilters[filterKey][index] =
							updatedFilter;
						state.processingFilters[filterKey] =
							sortProcessingFilters(
								state.processingFilters[filterKey]
							);
					}
					state.processingFilters.saveLoading = false;
				}
			)
			.addCase(
				updateProcessingFilterThunk.rejected,
				(state) => {
					state.processingFilters.saveLoading = false;
				}
			);

		builder
			.addCase(
				deleteProcessingFilterThunk.pending,
				(state) => {
					state.processingFilters.saveLoading = true;
				}
			)
			.addCase(
				deleteProcessingFilterThunk.fulfilled,
				(state, action) => {
					const filterId = action.payload.filterId;
					state.processingFilters.preProcessingFilters =
						sortProcessingFilters(
							state.processingFilters.preProcessingFilters.filter(
								(f: any) => f.filter_id !== filterId
							)
						);
					state.processingFilters.postProcessingFilters =
						sortProcessingFilters(
							state.processingFilters.postProcessingFilters.filter(
								(f: any) => f.filter_id !== filterId
							)
						);
					state.processingFilters.saveLoading = false;
				}
			)
			.addCase(
				deleteProcessingFilterThunk.rejected,
				(state) => {
					state.processingFilters.saveLoading = false;
				}
			);

		// Perf Processing Filters extra reducers
		builder
			.addCase(
				getPerfProcessingFiltersThunk.pending,
				(state, action) => {
					const requestedDatasetId = action.meta.arg;
					const isDatasetChanged =
						state.perfProcessingFilters.datasetId !==
						requestedDatasetId;

					if (isDatasetChanged) {
						state.perfProcessingFilters.preProcessingFilters =
							[];
						state.perfProcessingFilters.postProcessingFilters =
							[];
						state.perfProcessingFilters.hasFetched = false;
					}

					state.perfProcessingFilters.datasetId =
						requestedDatasetId;
					state.perfProcessingFilters.loading = true;
					state.perfProcessingFilters.error = null;
				}
			)
			.addCase(
				getPerfProcessingFiltersThunk.fulfilled,
				(state, action) => {
					const pre =
						action.payload.pre_processing_filters || {};
					const post =
						action.payload.post_processing_filters || {};
					state.perfProcessingFilters.preProcessingFilters =
						parseAndSortProcessingFilters(pre);
					state.perfProcessingFilters.postProcessingFilters =
						parseAndSortProcessingFilters(post);
					state.perfProcessingFilters.datasetId =
						action.meta.arg;
					state.perfProcessingFilters.hasFetched = true;
					state.perfProcessingFilters.loading = false;
					state.perfProcessingFilters.error = null;
				}
			)
			.addCase(
				getPerfProcessingFiltersThunk.rejected,
				(state, action) => {
					state.perfProcessingFilters.datasetId =
						action.meta.arg;
					state.perfProcessingFilters.hasFetched = false;
					state.perfProcessingFilters.loading = false;
					state.perfProcessingFilters.error =
						(action.payload as any)?.msg ||
						"Error loading filters";
				}
			);

		builder
			.addCase(
				addPerfProcessingFilterThunk.pending,
				(state) => {
					state.perfProcessingFilters.saveLoading = true;
				}
			)
			.addCase(
				addPerfProcessingFilterThunk.fulfilled,
				(state, action) => {
					const newFilter = action.payload.filter;
					if (newFilter.filter_type === "pre_processing") {
						state.perfProcessingFilters.preProcessingFilters.push(
							newFilter
						);
						state.perfProcessingFilters.preProcessingFilters =
							sortProcessingFilters(
								state.perfProcessingFilters
									.preProcessingFilters
							);
					} else {
						state.perfProcessingFilters.postProcessingFilters.push(
							newFilter
						);
						state.perfProcessingFilters.postProcessingFilters =
							sortProcessingFilters(
								state.perfProcessingFilters
									.postProcessingFilters
							);
					}
					state.perfProcessingFilters.saveLoading = false;
				}
			)
			.addCase(
				addPerfProcessingFilterThunk.rejected,
				(state) => {
					state.perfProcessingFilters.saveLoading = false;
				}
			);

		builder
			.addCase(
				updatePerfProcessingFilterThunk.pending,
				(state) => {
					state.perfProcessingFilters.saveLoading = true;
				}
			)
			.addCase(
				updatePerfProcessingFilterThunk.fulfilled,
				(state, action) => {
					const updatedFilter = action.payload.filter;
					const filterType = updatedFilter.filter_type;
					const filterKey =
						filterType === "pre_processing"
							? "preProcessingFilters"
							: "postProcessingFilters";

					const index = state.perfProcessingFilters[
						filterKey
					].findIndex(
						(f: any) =>
							f.filter_id === updatedFilter.filter_id
					);
					if (index !== -1) {
						state.perfProcessingFilters[filterKey][index] =
							updatedFilter;
						state.perfProcessingFilters[filterKey] =
							sortProcessingFilters(
								state.perfProcessingFilters[filterKey]
							);
					}
					state.perfProcessingFilters.saveLoading = false;
				}
			)
			.addCase(
				updatePerfProcessingFilterThunk.rejected,
				(state) => {
					state.perfProcessingFilters.saveLoading = false;
				}
			);

		builder
			.addCase(
				deletePerfProcessingFilterThunk.pending,
				(state) => {
					state.perfProcessingFilters.saveLoading = true;
				}
			)
			.addCase(
				deletePerfProcessingFilterThunk.fulfilled,
				(state, action) => {
					const filterId = action.payload.filterId;
					state.perfProcessingFilters.preProcessingFilters =
						sortProcessingFilters(
							state.perfProcessingFilters.preProcessingFilters.filter(
								(f: any) => f.filter_id !== filterId
							)
						);
					state.perfProcessingFilters.postProcessingFilters =
						sortProcessingFilters(
							state.perfProcessingFilters.postProcessingFilters.filter(
								(f: any) => f.filter_id !== filterId
							)
						);
					state.perfProcessingFilters.saveLoading = false;
				}
			)
			.addCase(
				deletePerfProcessingFilterThunk.rejected,
				(state) => {
					state.perfProcessingFilters.saveLoading = false;
				}
			);
	},
});

export const LedgerActions =
	ledgerSanitizationSlice.actions;
export const LedgerReducer =
	ledgerSanitizationSlice.reducer;
